<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { api } from '../api/client';
import type { QuerySource } from '../api/types';
import { useSessionStore } from '../stores/session';
import AppIcon from './AppIcon.vue';
import BaseSelect, { type SelectOption } from './BaseSelect.vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    source: QuerySource;
    placeholder?: string;
    defaultQuery?: string;
    allowedFields?: string[];
    showSubmit?: boolean;
    showReset?: boolean;
    syncUrl?: boolean;
    disabled?: boolean;
    exportable?: boolean;
    exportFrom?: number;
    exportUntil?: number;
  }>(),
  {
    placeholder: '',
    defaultQuery: '',
    allowedFields: () => [],
    showSubmit: true,
    showReset: false,
    syncUrl: true,
    disabled: false,
    exportable: false,
    exportFrom: undefined,
    exportUntil: undefined,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  submit: [];
  reset: [];
}>();

interface Suggestion {
  value: string;
  label: string;
  detail: string;
}

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
const { t } = useI18n();
const inputId = useId();
const exportTitleId = useId();
const exportFilenameId = useId();
const focused = ref(false);
const highlighted = ref(0);
const dynamicValues = ref<string[]>([]);
const exporting = ref(false);
const exportError = ref('');
const exportDialogOpen = ref(false);
const exportFormat = ref<'json' | 'csv'>('json');
const exportFilename = ref('');
const exportFilenameInput = ref<HTMLInputElement>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let valuesAbort: AbortController | null = null;
let requestGeneration = 0;

const fields: Record<QuerySource, Array<[string, string]>> = {
  issues: [
    ['status', 'status'],
    ['issue', 'issue_id'],
    ['title', 'title'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  errors: [
    ['level', 'level'],
    ['env', 'environment'],
    ['rel', 'release'],
    ['platform', 'platform'],
    ['issue', 'issue_id'],
    ['user', 'user.id'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  logs: [
    ['level', 'level'],
    ['msg', 'message'],
    ['svc', 'service'],
    ['env', 'environment'],
    ['rel', 'release'],
    ['trace', 'trace_id'],
    ['span', 'span_id'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  traces: [
    ['svc', 'service'],
    ['env', 'environment'],
    ['rel', 'release'],
    ['dur', 'duration_ms'],
    ['op', 'operation'],
    ['status', 'status'],
    ['trace', 'trace_id'],
    ['span', 'span_id'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  metrics: [
    ['metric', 'metric_name'],
    ['kind', 'metric_kind'],
    ['unit', 'unit'],
    ['trace', 'trace_id'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  replays: [
    ['replay', 'replay_id'],
    ['url', 'url'],
    ['env', 'environment'],
    ['rel', 'release'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  feedback: [
    ['feedback', 'feedback_id'],
    ['status', 'status'],
    ['msg', 'message'],
    ['replay', 'replay_id'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
  releases: [
    ['rel', 'release'],
    ['date', 'timestamp'],
    ['timestamp', 'timestamp'],
  ],
};

const enumValues: Record<string, string[]> = {
  'issues:status': ['open', 'resolved', 'ignored'],
  'feedback:status': ['open', 'resolved', 'spam'],
  'errors:level': ['debug', 'info', 'warning', 'error', 'fatal'],
  'logs:level': ['trace', 'debug', 'info', 'warning', 'error', 'fatal'],
  'metrics:metric_kind': ['counter', 'gauge', 'distribution'],
};
const orderedFields = new Set([
  'timestamp',
  'received_at',
  'duration_ms',
  'metric_count',
  'metric_sum',
  'metric_min',
  'metric_max',
]);

const activeToken = computed(() => props.modelValue.match(/(?:^|\s)([^\s()]*)$/)?.[1] ?? '');
const sourceFields = computed(() => {
  if (!props.allowedFields?.length) return fields[props.source];
  const allowed = new Set(props.allowedFields);
  return fields[props.source].filter(
    ([alias, canonical]) => allowed.has(alias) || allowed.has(canonical),
  );
});
const effectivePlaceholder = computed(
  () => props.placeholder || t(`unifiedQuery.placeholders.${props.source}`),
);
const activeField = computed(() => activeToken.value.split(':', 1)[0] ?? '');
const activeValue = computed(() => activeToken.value.split(':').slice(1).join(':'));
const activeCanonicalField = computed(
  () =>
    sourceFields.value.find(
      ([alias, canonical]) => alias === activeField.value || canonical === activeField.value,
    )?.[1] ?? '',
);
const chips = computed(
  () =>
    props.modelValue
      .match(/(?:[^\s"]+|"[^"]*")+/g)
      ?.filter((value) => value.includes(':') || value === 'AND' || value === 'OR') ?? [],
);
const suggestions = computed<Suggestion[]>(() => {
  const token = activeToken.value.toLowerCase();
  if (!token.includes(':')) {
    return sourceFields.value
      .filter(([alias, canonical]) => alias.startsWith(token) || canonical.startsWith(token))
      .slice(0, 20)
      .map(([alias, canonical]) => ({ value: `${alias}:`, label: alias, detail: canonical }));
  }
  const key = `${props.source}:${activeCanonicalField.value}`;
  const operators: Suggestion[] =
    !activeValue.value && orderedFields.has(activeCanonicalField.value)
      ? ['>', '>=', '<', '<=', '='].map((operator) => ({
          value: `${activeField.value}:${operator}`,
          label: operator,
          detail: 'operator',
        }))
      : [];
  const values = [...(enumValues[key] ?? []), ...dynamicValues.value]
    .filter((value, index, values) => values.indexOf(value) === index)
    .filter((value) => value.toLowerCase().startsWith(activeValue.value.toLowerCase()))
    .slice(0, 20)
    .map((value) => ({
      value: `${activeField.value}:${value}`,
      label: value,
      detail: activeField.value,
    }));
  return [...operators, ...values].slice(0, 20);
});
const open = computed(() => focused.value && suggestions.value.length > 0);
const exportFormatOptions = computed<SelectOption[]>(() => [
  {
    value: 'json',
    label: 'JSON',
    description: t('unifiedQuery.jsonDescription'),
    icon: 'code',
  },
  {
    value: 'csv',
    label: 'CSV',
    description: t('unifiedQuery.csvDescription'),
    icon: 'clipboard',
  },
]);

watch(activeToken, () => {
  highlighted.value = 0;
  dynamicValues.value = [];
  if (debounceTimer) clearTimeout(debounceTimer);
  valuesAbort?.abort();
  valuesAbort = null;
  const canonical = activeCanonicalField.value;
  if (!canonical || !['environment', 'release', 'metric_name'].includes(canonical)) return;
  const generation = ++requestGeneration;
  debounceTimer = setTimeout(async () => {
    const projectId = session.selectedProjectId;
    if (!projectId) return;
    valuesAbort = new AbortController();
    try {
      const result = await api.query<string>(
        projectId,
        {
          source: props.source,
          query: '',
          result: { kind: 'values', field: canonical },
          limit: 20,
        },
        valuesAbort.signal,
      );
      if (generation === requestGeneration) dynamicValues.value = result.items;
    } catch {
      if (generation === requestGeneration) dynamicValues.value = [];
    }
  }, 180);
});

watch(
  () => route.query.q,
  (value) => {
    if (!props.syncUrl) return;
    const query = typeof value === 'string' ? value : props.defaultQuery;
    if (query !== props.modelValue) {
      emit('update:modelValue', query);
      queueMicrotask(() => emit('submit'));
    }
  },
);

watch(exportFormat, (format) => {
  if (!exportDialogOpen.value) return;
  exportFilename.value = exportFilename.value.replace(/\.(?:json|csv)$/i, `.${format}`);
});

onBeforeUnmount(() => {
  requestGeneration += 1;
  if (debounceTimer) clearTimeout(debounceTimer);
  valuesAbort?.abort();
});

function replaceActiveToken(value: string): void {
  const start = props.modelValue.length - activeToken.value.length;
  emit('update:modelValue', `${props.modelValue.slice(0, start)}${value}`);
}

function selectSuggestion(suggestion: Suggestion): void {
  replaceActiveToken(suggestion.value);
  highlighted.value = 0;
}

function keydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !open.value && props.showSubmit) {
    event.preventDefault();
    void submit();
    return;
  }
  if (!open.value) return;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    highlighted.value = (highlighted.value + 1) % suggestions.value.length;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    highlighted.value =
      (highlighted.value - 1 + suggestions.value.length) % suggestions.value.length;
  } else if (event.key === 'Enter' && suggestions.value[highlighted.value]) {
    event.preventDefault();
    selectSuggestion(suggestions.value[highlighted.value]);
  } else if (event.key === 'Escape') focused.value = false;
}

function blur(): void {
  setTimeout(() => (focused.value = false), 120);
}

async function submit(): Promise<void> {
  if (props.syncUrl) {
    const query = props.modelValue.trim();
    const next = { ...route.query };
    if (query && query !== props.defaultQuery) next.q = query;
    else delete next.q;
    await router.replace({ query: next });
  }
  emit('submit');
}

async function reset(): Promise<void> {
  emit('update:modelValue', props.defaultQuery);
  if (props.syncUrl) {
    const next = { ...route.query };
    delete next.q;
    await router.replace({ query: next });
  }
  emit('reset');
}

function defaultExportFilename(format: 'json' | 'csv'): string {
  const type = props.source === 'traces' ? 'spans' : props.source;
  const filter = safeFilenameText(props.modelValue.trim().slice(0, 16))
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._]+|[._]+$/g, '');
  return `${type}_${filter || 'all'}.${format}`;
}

function safeFilenameText(value: string): string {
  const invalid = '<>:"/\\|?*';
  return [...value]
    .map((character) =>
      invalid.includes(character) || character.charCodeAt(0) < 32 ? '_' : character,
    )
    .join('');
}

function normalizedExportFilename(): string {
  const extension = `.${exportFormat.value}`;
  const value = safeFilenameText(exportFilename.value.trim()).replace(/[. ]+$/g, '');
  const basename =
    value.replace(/\.(?:json|csv)$/i, '') || defaultExportFilename(exportFormat.value);
  return basename.endsWith(extension) ? basename : `${basename}${extension}`;
}

function showExportDialog(): void {
  exportError.value = '';
  exportFormat.value = 'json';
  exportFilename.value = defaultExportFilename('json');
  exportDialogOpen.value = true;
  void nextTick(() => exportFilenameInput.value?.focus());
}

function closeExportDialog(): void {
  if (exporting.value) return;
  exportDialogOpen.value = false;
}

async function download(): Promise<void> {
  const projectId = session.selectedProjectId;
  if (!projectId || exporting.value) return;
  exporting.value = true;
  exportError.value = '';
  try {
    const result = await api.exportQuery(
      projectId,
      {
        source: props.source,
        query: props.modelValue.trim(),
        from: props.exportFrom,
        until: props.exportUntil,
        result: { kind: 'records' },
      },
      exportFormat.value,
    );
    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = normalizedExportFilename();
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    exportDialogOpen.value = false;
  } catch (error) {
    exportError.value = error instanceof Error ? error.message : t('unifiedQuery.exportFailed');
  } finally {
    exporting.value = false;
  }
}
</script>

<template>
  <div class="unified-query-bar" role="search">
    <div class="unified-query-bar__input-wrap">
      <AppIcon name="search" :size="17" />
      <label class="sr-only" :for="inputId">{{ t('unifiedQuery.label') }}</label>
      <input
        :id="inputId"
        :value="modelValue"
        type="search"
        :placeholder="effectivePlaceholder"
        maxlength="32768"
        autocomplete="off"
        spellcheck="false"
        :disabled="disabled"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="focused = true"
        @blur="blur"
        @keydown="keydown"
      />
      <ul v-if="open" class="unified-query-bar__suggestions" role="listbox">
        <li
          v-for="(suggestion, index) in suggestions"
          :key="`${suggestion.value}:${index}`"
          :class="{ 'is-active': index === highlighted }"
          role="option"
          :aria-selected="index === highlighted"
          @mousedown.prevent="selectSuggestion(suggestion)"
        >
          <code>{{ suggestion.label }}</code>
          <span>{{ suggestion.detail }}</span>
        </li>
      </ul>
    </div>
    <slot name="actions" />
    <button
      v-if="exportable"
      class="button button--secondary unified-query-bar__export"
      type="button"
      :disabled="disabled || exporting"
      @click="showExportDialog"
    >
      <AppIcon name="download" :size="16" />
      {{ t('unifiedQuery.export') }}
    </button>
    <button
      v-if="showSubmit"
      class="button button--primary"
      type="button"
      :disabled="disabled"
      @click="submit"
    >
      <AppIcon name="search" :size="16" />
      {{ t('common.search') }}
    </button>
    <button
      v-if="showReset"
      class="button button--secondary"
      type="button"
      :disabled="disabled"
      @click="reset"
    >
      <AppIcon name="close" :size="16" />
      {{ t('common.reset') }}
    </button>
  </div>
  <div
    v-if="chips.length"
    class="unified-query-bar__chips"
    :aria-label="t('unifiedQuery.conditions')"
  >
    <code v-for="chip in chips" :key="chip">{{ chip }}</code>
  </div>

  <Teleport to="body">
    <div
      v-if="exportDialogOpen"
      class="export-dialog-backdrop"
      @click.self="closeExportDialog"
      @keydown.esc="closeExportDialog"
    >
      <form
        class="panel export-dialog"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="exportTitleId"
        @submit.prevent="download"
      >
        <header class="export-dialog__header">
          <div>
            <p class="eyebrow">{{ t('unifiedQuery.exportEyebrow') }}</p>
            <h2 :id="exportTitleId">{{ t('unifiedQuery.exportTitle') }}</h2>
          </div>
          <button
            class="icon-button"
            type="button"
            :aria-label="t('common.close')"
            :disabled="exporting"
            @click="closeExportDialog"
          >
            <AppIcon name="close" :size="17" />
          </button>
        </header>

        <BaseSelect
          v-model="exportFormat"
          :options="exportFormatOptions"
          :label="t('unifiedQuery.exportFormat')"
          :disabled="exporting"
        />

        <label :for="exportFilenameId">
          {{ t('unifiedQuery.exportFilename') }}
          <input
            :id="exportFilenameId"
            ref="exportFilenameInput"
            v-model="exportFilename"
            required
            maxlength="180"
            autocomplete="off"
            spellcheck="false"
            :disabled="exporting"
          />
        </label>

        <p v-if="exportError" class="field-error" role="alert">{{ exportError }}</p>

        <footer class="export-dialog__actions">
          <button
            class="button button--secondary"
            type="button"
            :disabled="exporting"
            @click="closeExportDialog"
          >
            {{ t('common.cancel') }}
          </button>
          <button class="button button--primary" type="submit" :disabled="exporting">
            <AppIcon :name="exporting ? 'loading' : 'download'" :size="16" />
            {{ exporting ? t('unifiedQuery.exporting') : t('unifiedQuery.download') }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
</template>
